import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy } from 'react';
import { Toaster } from 'react-hot-toast';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { LocaleContextProvider } from './contexts/LocaleContext';

// Layout components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Page components
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Users from './pages/Users';
import Dashboard from './pages/Dashboard';
import Facilities from './pages/Facilities';
import FacilityTypesManagement from './pages/FacilityTypesManagement';
import Roles from './pages/Roles';
import AssessmentsList from './pages/AssessmentsList';
import NewAssessment from './pages/NewAssessment';
import AssessmentDetailPage from './pages/AssessmentDetailPage';
import Profile from './pages/Profile';
import QuestionManagement from './pages/QuestionManagement';
import QuestionnaireDetailPage from './pages/QuestionnaireDetailPage';
import ComplianceQuestionnaireDetailPage from './pages/ComplianceQuestionnaireDetailPage';
import AppealPage from './pages/AppealPage';
import Appeals from './pages/Appeals';
import ChangePassword from './pages/ChangePassword';
import DecisionsAnalytics from './pages/DecisionsAnalytics';
import Recommendations from './pages/Recommendations';
import Surveys from './pages/Surveys';
import SurveyDetail from './pages/SurveyDetail';

// Beverage authorization module — lazy-loaded (App.tsx is already heavy with static imports)
const BeverageAuthorizations = lazy(() => import('./pages/BeverageAuthorizations'));
const BeverageAuthorizationDetail = lazy(() => import('./pages/BeverageAuthorizationDetail'));
const BeverageAssessmentPage = lazy(() => import('./pages/BeverageAssessmentPage'));
const BeverageInspectionPage = lazy(() => import('./pages/BeverageInspectionPage'));
const BeverageDashboard = lazy(() => import('./pages/BeverageDashboard'));
const BeverageMonitoring = lazy(() => import('./pages/BeverageMonitoring'));
const BeverageTypes = lazy(() => import('./pages/BeverageTypes'));
const BeverageInspectionQuestionnaires = lazy(() => import('./pages/BeverageInspectionQuestionnaires'));
const BeverageInspectionQuestionnaireDetailPage = lazy(() => import('./pages/BeverageInspectionQuestionnaireDetailPage'));
const SmsTemplates = lazy(() => import('./pages/SmsTemplates'));

const lazyPage = (element: React.ReactNode) => (
  <Suspense fallback={<div className='p-8 text-center text-sm text-gray-400'>Loading…</div>}>{element}</Suspense>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, 
      retry: 2,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LocaleContextProvider>
        <Router>
          <Routes>
            <Route path='/login' element={<Login />} />
            <Route path='/register' element={<Register />} />
            <Route path='/forgot-password' element={<ForgotPassword />} />
            <Route path='/appeals/:assessmentId' element={<AppealPage />} />
            <Route
              path='/change-password'
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />

            <Route
              path='/'
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to='/dashboard' replace />} />
              <Route path='dashboard' element={<Dashboard />} />
              <Route path='users' element={<Users />} />
              <Route path='facilities' element={<Facilities />} />
              <Route path='facility-types' element={<FacilityTypesManagement />} />
              <Route path='questions' element={<QuestionManagement />} />
              <Route path='questions/hygiene/:questionnaireId' element={<QuestionnaireDetailPage />} />
              <Route path='questions/compliance/:questionnaireId' element={<ComplianceQuestionnaireDetailPage />} />
              <Route path='roles' element={<Roles />} />
              <Route path='assessments' element={<AssessmentsList />} />
              <Route path='assessments/new' element={<NewAssessment />} />
              <Route path='assessments/:id' element={<AssessmentDetailPage />} />
              <Route path='profile' element={<Profile />} />
              <Route path='appeals' element={<Appeals />} />
              <Route path='decisions-analytics' element={<DecisionsAnalytics />} />
              <Route path='recommendations' element={<Recommendations />} />
              <Route path='surveys' element={<Surveys />} />
              <Route path='surveys/:id' element={<SurveyDetail />} />
              <Route path='beverage-authorizations' element={lazyPage(<BeverageAuthorizations />)} />
              <Route path='beverage-authorizations/dashboard' element={lazyPage(<BeverageDashboard />)} />
              <Route path='beverage-authorizations/:id' element={lazyPage(<BeverageAuthorizationDetail />)} />
              <Route path='beverage-authorizations/:id/assess' element={lazyPage(<BeverageAssessmentPage />)} />
              <Route path='beverage-authorizations/:id/inspect/:phase' element={lazyPage(<BeverageInspectionPage />)} />
              <Route path='beverage-monitoring' element={lazyPage(<BeverageMonitoring />)} />
              <Route path='beverage-types' element={lazyPage(<BeverageTypes />)} />
              <Route path='beverage-inspection-questionnaires' element={lazyPage(<BeverageInspectionQuestionnaires />)} />
              <Route path='beverage-inspection-questionnaires/:questionnaireId' element={lazyPage(<BeverageInspectionQuestionnaireDetailPage />)} />
              <Route path='sms-templates' element={lazyPage(<SmsTemplates />)} />
            </Route>
          </Routes>
        </Router>
        <Toaster
          position='bottom-right'
          toastOptions={{
            duration: 4000,
          }}
        />
      </LocaleContextProvider>
    </QueryClientProvider>
  );
}

export default App;
